// エラーを events.jsonl に載せる唯一の場所 (spec 2026-07-29 §3.1)。
//
// ここが throw すると「エラー報告がエラーを生む」最悪の失敗モードになるので、
// 公開関数はすべて内部を try/catch で包み、呼び出し側へ例外を漏らさない
// (CLAUDE.md 原則 5: 観測のために本編を止めない)。

import { eventBus } from "./eventBus";
import { makeEvent } from "./eventSchema";
import { useChatStore } from "../state/chatStore";
import type { AppErrorCode } from "../errors/types";

const MESSAGE_CAP = 200;
const STACK_CAP = 300;
const STACK_MAX_LINES = 3;

export function truncate(text: string, max: number): string {
  return text.length <= max ? text : text.slice(0, max);
}

/**
 * minify 済みの stack は数 KB になるが、原因の特定には先頭数フレームで足りる。
 * beacon の 48KB cap を圧迫しないよう行数と文字数の両方で切る (D9)。
 */
export function truncateStack(stack: string | undefined): string | undefined {
  if (!stack) return undefined;
  return truncate(stack.split("\n").slice(0, STACK_MAX_LINES).join("\n"), STACK_CAP);
}

/**
 * D4: 既存の emit 点 (vadController.ts の onSpeechStart 等) と同じ gate。
 *
 * sessionId が無いまま emit すると SessionEventSchema.sessionId (min(1)) を
 * 満たさない行ができ、サーバ (functions/api/sessions/[[path]].ts) が
 * events.jsonl 全体を 400 で拒否する。1 件を記録するために全部を失うのは
 * 割に合わないので、session 確立前のエラーは捨てる (spec §9.5)。
 */
function emitSafely(type: string, payload: Record<string, unknown>): void {
  try {
    const sessionId = useChatStore.getState().sessionId;
    if (!sessionId) return;
    eventBus.emit(makeEvent(type, sessionId, payload));
  } catch {
    /* 観測のために本編を壊さない (原則 5) */
  }
}

/**
 * API client 層から呼ぶ。`reason` で「サーバがエラーを返した」と
 * 「そもそも届かなかった」を区別する — 後者は現状まったく痕跡が残らない
 * 経路で、最も知りたい失敗でもある (spec §1.3(a))。
 */
export function reportAppError(input: {
  code: AppErrorCode;
  reason: "network" | "http";
  context: string;
  message: string;
  httpStatus?: number;
}): void {
  emitSafely("app.error", {
    code: input.code,
    reason: input.reason,
    context: input.context,
    message: truncate(input.message, MESSAGE_CAP),
    ...(input.httpStatus !== undefined ? { httpStatus: input.httpStatus } : {}),
  });
}

function toError(value: unknown): { name: string; message: string; stack?: string } {
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  return { name: "UnknownError", message: String(value) };
}

/**
 * window.onerror / unhandledrejection を購読し app.crash を emit する。
 * App の mount で呼び、返り値を unmount で呼ぶ (attachEventDebugLogger と同じ形)。
 */
export function attachCrashReporter(): () => void {
  const onError = (ev: ErrorEvent) => {
    const e = toError(ev.error ?? ev.message);
    const stack = truncateStack(e.stack);
    emitSafely("app.crash", {
      kind: "error",
      name: e.name,
      message: truncate(ev.message || e.message, MESSAGE_CAP),
      ...(stack ? { stack } : {}),
      ...(ev.filename ? { source: ev.filename } : {}),
      ...(typeof ev.lineno === "number" && ev.lineno > 0 ? { line: ev.lineno } : {}),
    });
  };

  const onRejection = (ev: Event) => {
    const e = toError((ev as Event & { reason?: unknown }).reason);
    const stack = truncateStack(e.stack);
    emitSafely("app.crash", {
      kind: "unhandledrejection",
      name: e.name,
      message: truncate(e.message, MESSAGE_CAP),
      ...(stack ? { stack } : {}),
    });
  };

  window.addEventListener("error", onError as EventListener);
  window.addEventListener("unhandledrejection", onRejection);
  return () => {
    window.removeEventListener("error", onError as EventListener);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}
